import React, { useCallback, useEffect, useState } from "react";

import { useDataProvider, useNotify } from "react-admin";
import {
	Box,
	Button,
	ButtonBase,
	Card,
	CardContent,
	CircularProgress,
	Collapse,
	Grid,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BuildIcon from "@mui/icons-material/Build";
import dateFormat from "dateformat";

import _ from "lodash";
import { getFunctions, httpsCallable } from "firebase/functions";
import { firebaseApp } from "../config/firebaseAuthProvider";
import { ExpandLess, Refresh } from "@mui/icons-material";
import { useBuildStatusListener } from "../layout/BuildStatusListener";

const StatusCard = ({}) => {
	const { fetchWorkflowRuns, workflowRuns, isExpanded, setIsExpanded, isLoading } =
		useBuildStatusListener();

	useEffect(() => {
		fetchWorkflowRuns();
	}, []);

	const handleExpandClick = () => {
		setIsExpanded(!isExpanded);
	};

	return (
		<Card>
			<CardContent>
				<Box display="flex" alignItems="center">
					<Box marginRight={2}>
						<ButtonBase onClick={handleExpandClick}>
							<Typography variant="h6" flex={1}>
								Derniers builds
							</Typography>
							{isExpanded ? <ExpandLess color="primary" /> : <ExpandMoreIcon color="primary" />}
						</ButtonBase>
					</Box>

					<Button
						onClick={() => fetchWorkflowRuns(true)}
						variant="outlined"
						startIcon={<Refresh />}
						disabled={isLoading}
						size="small"
					>
						Actualiser
					</Button>
				</Box>

				{isLoading ? (
					<Box display="flex" alignItems="center" justifyContent="center" height={200}>
						<CircularProgress />
					</Box>
				) : (
					<Collapse in={isExpanded}>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>Date</TableCell>
									<TableCell>Status</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{workflowRuns &&
									workflowRuns.map((run) => (
										<TableRow key={run.id}>
											<TableCell>{dateFormat(run.created_at, "dd/mm/yyyy HH:MM:ss")}</TableCell>
											<TableCell>{run.status}</TableCell>
										</TableRow>
									))}
							</TableBody>
						</Table>
					</Collapse>
				)}
			</CardContent>
		</Card>
	);
};

const Dashboard = ({}) => {
	const dataProvider = useDataProvider();

	const [donations, setDonations] = useState([]);
	const [totalIncome, setTotalIncome] = useState(0);
	const [affectedIncome, setAffectedIncome] = useState(0);
	const [unaffectedIncome, setUnaffectedIncome] = useState(0);
	const [donationsLoading, setDonationsLoading] = useState(true);
	const [detailedAffectedIncomeCampaigns, setDetailedAffectedIncomeCampaigns] = useState({});
	const [detailedUnaffectedIncomeCampaigns, setDetailedUnaffectedIncomeCampaigns] = useState({});

	const [campaigns, setCampaigns] = useState([]);
	const [activeCampaigns, setActiveCampaigns] = useState(0);
	const [incomingCampaigns, setIncomingCampaigns] = useState(0);
	const [terminatedCampaigns, setTerminatedCampaigns] = useState(0);
	const [campaignsLoading, setCampaignsLoading] = useState(true);
	const [detailedCampaigns, setDetailedCampaigns] = useState({});

	const [expanded, setExpanded] = useState(false);
	const notify = useNotify();

	const fetchDonations = useCallback(async () => {
		try {
			const perPage = 50;
			let page = 1;
			while (true) {
				const { data: donations, total } = await dataProvider.getList("donation", {
					pagination: { page, perPage },
					sort: { field: "created_date", order: "asc" },
				});

				setDonations(donations);

				if (page * perPage > total) {
					break;
				} else {
					page = page + 1;
				}
			}
			setDonations([]);
			setDonationsLoading(false);
		} catch (e) {
			console.error(e);
			setDonationsLoading(false);
		}
	}, [dataProvider]);

	const fetchCampaigns = useCallback(async () => {
		try {
			const perPage = 50;
			let page = 1;
			while (true) {
				const { data: campaigns, total } = await dataProvider.getList("campaign", {
					pagination: { page, perPage },
					sort: { field: "created_date", order: "asc" },
				});

				setCampaigns(campaigns);

				if (page * perPage > total) {
					break;
				} else {
					page = page + 1;
				}
			}
			setCampaigns([]);
			setCampaignsLoading(false);
		} catch (e) {
			console.error(e);
			setCampaignsLoading(false);
		}
	}, [dataProvider]);

	useEffect(() => {
		const _donations = _.clone(donations);
		const _affectedDonations = _.filter(
			_donations,
			({ affectation_code }) => affectation_code !== "999990"
		);
		const _unaffectedDonations = _.filter(
			_donations,
			({ affectation_code }) => affectation_code === "999990"
		);

		setTotalIncome((totalIncome) =>
			_.reduce(
				_.map(_donations, ({ amount }) => amount),
				(acc, a) => acc + a,
				totalIncome
			)
		);
		setAffectedIncome((affectedIncome) =>
			_.reduce(
				_.map(_affectedDonations, ({ amount }) => amount),
				(acc, a) => acc + a,
				affectedIncome
			)
		);
		setUnaffectedIncome((unaffectedIncome) =>
			_.reduce(
				_.map(_unaffectedDonations, ({ amount }) => amount),
				(acc, a) => acc + a,
				unaffectedIncome
			)
		);

		setDetailedAffectedIncomeCampaigns((detailedAffectedIncomeCampaigns) =>
			_.assignWith(
				detailedAffectedIncomeCampaigns,
				..._.map(_affectedDonations, (d) => ({ [d.campaign_id]: d.amount })),
				(objValue, srcValue) => (_.isUndefined(objValue) ? srcValue : objValue + srcValue)
			)
		);
		setDetailedUnaffectedIncomeCampaigns((detailedUnaffectedIncomeCampaigns) =>
			_.assignWith(
				detailedUnaffectedIncomeCampaigns,
				..._.map(_unaffectedDonations, ({ campaign_id, amount }) => ({ [campaign_id]: amount })),
				(objValue, srcValue) => (_.isUndefined(objValue) ? srcValue : objValue + srcValue)
			)
		);
	}, [donations]);

	useEffect(() => {
		const _campaigns = _.clone(campaigns);
		const _activeCampaigns = _.filter(
			_campaigns,
			({ status }) => status === "CAMPAIGN_STATUS_IN_PROGRESS"
		);
		const _incomingCampaigns = _.filter(
			_campaigns,
			({ status }) => status === "CAMPAIGN_STATUS_FORTHCOMING"
		);
		const _terminatedCampaigns = _.filter(
			_campaigns,
			({ status }) => status === "CAMPAIGN_STATUS_COMPLETED"
		);

		setActiveCampaigns((activeCampaigns) => activeCampaigns + _activeCampaigns.length);
		setIncomingCampaigns((incomingCampaigns) => incomingCampaigns + _incomingCampaigns.length);
		setTerminatedCampaigns(
			(terminatedCampaigns) => terminatedCampaigns + _terminatedCampaigns.length
		);

		setDetailedCampaigns((detailedCampaigns) =>
			_.assign(
				detailedCampaigns,
				..._.map(_campaigns, ({ id, abstract_title }) => ({ [id]: abstract_title }))
			)
		);
	}, [campaigns]);

	useEffect(() => {
		fetchDonations();
		fetchCampaigns();
	}, [fetchDonations, fetchCampaigns]);

	const renderDetailedCampaigns = (d) =>
		_.isEmpty(d) ? null : (
			<>
				<div>
					<Button
						onClick={() => {
							setExpanded(!expanded);
						}}
					>
						Afficher les détails
						<ExpandMoreIcon style={expanded ? { transform: "rotate(180deg)" } : undefined} />
					</Button>
				</div>
				<Collapse in={expanded} timeout="auto" unmountOnExit>
					<Table size="small">
						<TableBody>
							{_.map(
								_.filter(d, (v, k) => k === "null"),
								(income, campaignId) => (
									<TableRow key={campaignId}>
										<TableCell>
											<b>Non affecté à une campagne</b>
										</TableCell>
										<TableCell align="right">{income}€</TableCell>
									</TableRow>
								)
							) || null}
							{_.map(d, (income, campaignId) =>
								_.isEmpty(campaignId) || campaignId === "null" ? null : (
									<TableRow key={campaignId}>
										<TableCell>{detailedCampaigns[campaignId] || <CircularProgress />}</TableCell>
										<TableCell align="right">{income}€</TableCell>
									</TableRow>
								)
							)}
						</TableBody>
					</Table>
				</Collapse>
			</>
		);

	const { setCalledBuildFunction } = useBuildStatusListener();
	const buildUsingApi = () => {
		const functions = getFunctions(firebaseApp);
		const callableBuildApp = httpsCallable(functions, "app/build");

		callableBuildApp({
			branch:
				process.env.REACT_APP_ENV === "staging"
					? "staging"
					: process.env.REACT_APP_ENV === "production"
					? "master"
					: "",
		})
			.then(() => {
				console.log("Build function start");
				notify("Le build est lancé. Cela peut prendre plusieurs minutes...", {
					type: "success",
					autoHideDuration: 1000 * 60 * 10,
					multiLine: true,
				});
				setCalledBuildFunction(new Date());
			})
			.catch((error) => {
				notify(
					"Le lancement du build a rencontré un problème. Veuillez contacter l'administrateur",
					{ type: "error" }
				);
				console.log(error);
			});
	};

	return (
		<>
			<Box m="1em">
				<Card>
					<CardContent>
						<Typography variant="h4" gutterBottom>
							Bienvenue dans l'administration de Défis de Malte
						</Typography>
						<Button
							size="small"
							color="primary"
							component={"a"}
							startIcon={<DescriptionIcon />}
							variant="contained"
							target="_blank"
							href="https://docs.adfinitas.io/defis-malte/"
						>
							Accès à la documentation
						</Button>
						<br />
						<br />
						<Button
							size="small"
							color="primary"
							startIcon={<BuildIcon />}
							variant="contained"
							onClick={buildUsingApi}
						>
							Lancer le build
						</Button>
					</CardContent>
				</Card>
				<StatusCard />
			</Box>
			<Grid container>
				<Grid item xs={4}>
					<Box m="1em">
						<Card>
							<CardContent>
								<Typography variant="h6">Revenus bruts</Typography>
								<Typography variant="h3">
									{donationsLoading ? <CircularProgress /> : null}
									{totalIncome} €
								</Typography>
							</CardContent>
						</Card>
					</Box>
				</Grid>
				<Grid item xs={4}>
					<Box m="1em">
						<Card>
							<CardContent>
								<Typography variant="h6">Revenus bruts affectés</Typography>
								<Typography variant="h3">
									{donationsLoading ? <CircularProgress /> : null}
									{affectedIncome} €
								</Typography>
								{renderDetailedCampaigns(detailedAffectedIncomeCampaigns)}
							</CardContent>
						</Card>
					</Box>
				</Grid>
				<Grid item xs={4}>
					<Box m="1em">
						<Card>
							<CardContent>
								<Typography variant="h6">Revenus bruts non-affectés</Typography>
								<Typography variant="h3">
									{donationsLoading ? <CircularProgress /> : null}
									{unaffectedIncome} €
								</Typography>
								{renderDetailedCampaigns(detailedUnaffectedIncomeCampaigns)}
							</CardContent>
						</Card>
					</Box>
				</Grid>
				<Grid item xs={4}>
					<Box m="1em">
						<Card>
							<CardContent>
								<Typography variant="h6">Nombre de campagnes actives</Typography>
								<Typography variant="h3">
									{campaignsLoading ? <CircularProgress /> : null}
									{activeCampaigns}
								</Typography>
							</CardContent>
						</Card>
					</Box>
				</Grid>
				<Grid item xs={4}>
					<Box m="1em">
						<Card>
							<CardContent>
								<Typography variant="h6">Nombre de campagnes à venir</Typography>
								<Typography variant="h3">
									{campaignsLoading ? <CircularProgress /> : null}
									{incomingCampaigns}
								</Typography>
							</CardContent>
						</Card>
					</Box>
				</Grid>
				<Grid item xs={4}>
					<Box m="1em">
						<Card>
							<CardContent>
								<Typography variant="h6">Nombre de campagnes terminées</Typography>
								<Typography variant="h3">
									{campaignsLoading ? <CircularProgress /> : null}
									{terminatedCampaigns}
								</Typography>
							</CardContent>
						</Card>
					</Box>
				</Grid>
			</Grid>
		</>
	);
};

export default Dashboard;
